// LA CAJA DEL AGENTE EN LA HOME — ADR-2026-09-21-la-caja-de-la-home-es-la-del-agente.
//
// Es `BarraEntradaChat`, la MISMA barra de entrada que usan el panel lateral y la
// pantalla dedicada, montada en la home: el "+ Agregar" con su menú y sus selectores del
// catálogo, el selector de Inteligencia, el micrófono y el botón de enviar. La home tuvo
// unas horas un `textarea` propio con un botón de enviar al lado y ahí se veía el
// problema: la caja PARECÍA la del Agente y no hacía nada de lo que esa caja hace, así
// que adjuntar un archivo o bajar la inteligencia obligaba a entrar al chat y volver a
// escribir el pedido.
//
// ── POR QUÉ ES UN COMPONENTE APARTE Y NO PARTE DEL HERO ───────────────────
// Acá adentro se lee el contexto GRANDE del chat (`useChatAtlas`), que cambia de
// identidad en cada token del stream. Aislado en este componente, lo que se vuelve a
// renderizar mientras el agente escribe en el panel de al lado es esta caja y nada más;
// si el hero lo leyera, se re-renderizarían con cada token el switch, la grilla de modos
// y la home entera. Es la misma razón por la que el contexto está partido en dos.
//
// El borrador es EL MISMO del chat (`chat.borrador`), no uno propio: lo que se empieza a
// escribir acá sigue estando al entrar al Agente, y al revés.

import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useChatAtlas } from "../ChatAtlasContext";
import { useEntornoChatAtlas } from "../entornoContext";
import { ALTO_DE_LA_FILA_DE_FUENTES, BarraEntradaChat } from "./BarraEntradaChat";
import {
  IDEAS_DEL_PLACEHOLDER,
  PLACEHOLDER_QUIETO,
  usePlaceholderQueSeEscribe,
} from "./placeholderQueSeEscribe";
import {
  SelectoresDeAdjuntos,
  type SelectorAbierto,
} from "./SelectoresDeAdjuntos";
import { ModalDeFuentes } from "./ModalDeFuentes";

/**
 * LA TARJETA "AGREGAR PRODUCTO" — ADR-2026-09-21-la-caja-de-la-home-abre-el-selector-de-fuentes.
 *
 * Vive ADENTRO de la caja, arriba del placeholder y de lo que se escribe, a la izquierda
 * de los chips de lo enganchado (ADR-2026-09-22-la-tarjeta-va-arriba-del-texto,
 * ADR-2026-09-22-lo-enganchado-va-al-lado-de-la-tarjeta), y abre el selector de fuentes
 * con pestañas.
 *
 * Es un CUADRADO —el "+" arriba, el rótulo abajo— y es la que manda la medida de la fila:
 * los chips se estiran a su alto, no al revés
 * (ADR-2026-09-22-los-chips-miden-lo-mismo-que-la-tarjeta). El lado sale de
 * `ALTO_DE_LA_FILA_DE_FUENTES`, la misma constante con la que la barra estira los chips:
 * con una copia de cada lado, mover una dejaba la fila despareja.
 *
 * El COLOR es lo propio: azul clarito —el de "Invitá y ganá" de la home y de la pastilla
 * activa de la biblioteca— porque es un CONTROL y no un adjunto, y tiene que destacar un
 * poco sobre el gris de los chips. Tuvo además un aro azul; se fue a pedido
 * (ADR-2026-09-25-la-tarjeta-de-fuentes-es-mas-ancha-en-el-telefono).
 */
/** El ancho de la tarjeta en el teléfono: "Agregar imagen" a 11px en un renglón, con
 *  el aire de los lados. El alto sigue siendo el de la fila (68). */
const ANCHO_EN_EL_TELEFONO = 116;

const TarjetaDeFuentes: React.FC<{ onAbrir: () => void; chica: boolean }> = ({
  onAbrir,
  chica,
}) => {
  const lado = ALTO_DE_LA_FILA_DE_FUENTES[chica ? "panel" : "pantalla"];
  return (
    <button
      type="button"
      onClick={onAbrir}
      aria-label="Agregar imagen"
      // En el teléfono el cuadrado mide 68 y no 92, así que el aire de adentro baja de 10
      // a 8: son los 4px que le faltaban al rótulo entero para entrar en dos renglones.
      // Sin aro: era un `ring` azul que la separaba de los chips, y se pidió que se
      // vaya en los dos tamaños (ADR-2026-09-25-la-tarjeta-de-fuentes-es-mas-ancha-en-el-telefono).
      // El fondo azul clarito y el "+" en su círculo alcanzan para decir que es un control.
      className={`group flex shrink-0 flex-col items-start justify-between rounded-2xl bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-left transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.135)] ${
        chica ? "p-2" : "p-2.5"
      }`}
      // En el teléfono es MÁS ANCHA que alta
      // (ADR-2026-09-25-la-tarjeta-de-fuentes-es-mas-ancha-en-el-telefono): con 68 de
      // lado el rótulo iba a dos renglones de 10px y la tarjeta se veía apretada. El
      // alto no cambia —es el de los chips de al lado— y el ancho no le importa a nadie
      // más: en el teléfono la fila es una tira que scrollea.
      style={{ height: lado, width: chica ? ANCHO_EN_EL_TELEFONO : lado }}
    >
      <span
        // El "+" también se achica en el teléfono (24 contra 28): es lo otro que había
        // que ceder para que el rótulo entre entero, y a 24 el ícono sigue siendo el
        // mismo gesto.
        className={`inline-flex items-center justify-center rounded-full bg-white text-brand-primary shadow-sm ${
          chica ? "h-6 w-6" : "h-7 w-7"
        }`}
      >
        <Plus className={chica ? "h-3.5 w-3.5" : "h-4 w-4"} strokeWidth={2.25} aria-hidden="true" />
      </span>
      {/* EL RÓTULO ENTERO EN LOS DOS TAMAÑOS —
          ADR-2026-09-22-la-tarjeta-dice-agregar-producto-tambien-en-el-telefono. En el
          teléfono decía sólo "Producto": la tarjeta se leía a medio terminar, y el verbo
          —lo único que dice que ESTO AGREGA algo— quedaba únicamente en el "+". Con la
          tarjeta más ancha en el teléfono entra en UN renglón al mismo tamaño que en
          escritorio; ya no hace falta ceder tipografía. En escritorio la tarjeta sigue
          siendo un cuadrado de 92 y el rótulo va en dos renglones, como siempre: forzarlo
          a uno lo hacía desbordar. */}
      <span
        className={`text-[11px] font-semibold leading-tight text-brand-primary ${
          chica ? "whitespace-nowrap" : ""
        }`}
      >
        Agregar imagen
      </span>
    </button>
  );
};

export interface CajaDelAgenteHomeProps {
  /**
   * El plan no incluye el Agente (`getAgenteLock`). La caja se dibuja igual —es lo que
   * explica qué se está perdiendo— pero enviar no manda: llama a `onBloqueado`.
   */
  bloqueado?: boolean;
  onBloqueado?: () => void;
  /** Se llama DESPUÉS de mandar (o de encolar): la home navega al Agente. */
  onEnviado: () => void;
  /** `pantalla` (desktop): caja alta, "+ Agregar" con rótulo y menú de 3 cards por fila.
   *  `panel` (teléfono): la caja crece con el texto y el "+" va sin rótulo. */
  variante: "panel" | "pantalla";
  /**
   * Un pedido de COMPLETAR la caja desde afuera (los pills de sugerencia del hero). El
   * `token` es un contador y no un booleano para que la misma sugerencia dos veces
   * también complete y enfoque. Completa y NO manda
   * (ADR-2026-09-12-las-sugerencias-completan-la-caja).
   */
  sugerencia?: { texto: string; token: number };
}

export const CajaDelAgenteHome: React.FC<CajaDelAgenteHomeProps> = ({
  bloqueado = false,
  onBloqueado,
  onEnviado,
  variante,
  sugerencia,
}) => {
  const chat = useChatAtlas();
  const entorno = useEntornoChatAtlas();
  const { companyId } = entorno;
  // El selector del catálogo abierto (producto / modelo / pose / fondo / imagen ya
  // generada). Acá NO hay biblioteca lateral como en la pantalla dedicada, así que las
  // cards del "+" abren el selector modal, que es lo que hace también el panel.
  const [selector, setSelector] = useState<SelectorAbierto>(null);
  // El selector de FUENTES con pestañas, el que abre la tarjeta de la izquierda
  // (ADR-2026-09-21-la-caja-de-la-home-abre-el-selector-de-fuentes). Es otro camino que
  // el de las cards del "+": ahí cada card abre el selector de SU tipo, y acá las seis
  // fuentes están en pestañas de un mismo pop-up.
  const [fuentes, setFuentes] = useState(false);
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0);

  // EL PLACEHOLDER SE ESCRIBE SOLO, proponiendo ideas — y sólo acá, que es la caja de la
  // home (ADR-2026-09-21-el-placeholder-de-la-home-propone-ideas). En el chat la caja ya
  // está en contexto y un texto que se mueve al lado de la conversación distrae.
  //
  // `PLACEHOLDER_QUIETO` es la PRIMERA idea de la rotación, no una frase aparte
  // (ADR-2026-09-21-el-placeholder-quieto-es-una-idea-mas): es lo que se ve con
  // `prefers-reduced-motion` —donde no se escribe nada— y lo que se repone si el ciclo
  // se corta a mitad de una frase.
  const cajaRef = useRef<HTMLDivElement>(null);
  usePlaceholderQueSeEscribe(
    cajaRef,
    IDEAS_DEL_PLACEHOLDER,
    // Con algo escrito el placeholder ni se ve, y con un lote de fotos enganchado la
    // barra pone el suyo ("Contame qué hacer con las fotos"), que no hay que pisar.
    !chat.borrador && !chat.loteFotos,
    PLACEHOLDER_QUIETO,
  );

  // Una sugerencia completa la caja y le pide el foco al textarea. El efecto mira el
  // `token` y no el texto: dos veces la misma sugerencia tiene que volver a enfocar.
  const token = sugerencia?.token ?? 0;
  const setBorrador = chat.setBorrador;
  useEffect(() => {
    if (!token || !sugerencia) return;
    setBorrador(sugerencia.texto);
    setPedidoDeFoco((n) => n + 1);
    // `sugerencia` completo fuera de las dependencias a propósito: manda el token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, setBorrador]);

  return (
    <>
      {/* El envoltorio existe para el placeholder que se escribe solo: es desde donde el
          hook encuentra el `textarea`. No cambia ni la medida ni la posición de la caja.

          Y es también desde donde se PINTA ese placeholder, más oscuro que el de siempre
          (ADR-2026-09-21-el-placeholder-de-la-home-se-lee-2). La barra lo dibuja en
          `#02062B` al 38%, que es lo correcto para un rótulo quieto que dice qué va en la
          caja; acá el placeholder no es un rótulo sino una propuesta que se escribe sola,
          y a ese gris la frase se perdía justo cuando pedía que la leyeran. Va al 60%:
          se lee de un vistazo y todavía es visiblemente más claro que el texto escrito
          (90%), así que no se confunde con algo ya tipeado.

          Va como variante desde el envoltorio y no como prop nueva de `BarraEntradaChat`
          por lo mismo que el hook busca el `textarea` en vez de pedir su ref: la barra la
          usan también el panel y la pantalla del chat, donde el placeholder es un rótulo
          quieto y tiene que seguir siendo el de siempre. La regla que sale de acá es
          `.envoltorio textarea::placeholder`, que le gana por especificidad a la clase
          que la barra le pone al `textarea`. */}
      {/* Y en el TELÉFONO el placeholder va además más chico
          (ADR-2026-09-21-el-texto-del-hero-se-achica-en-el-telefono): las frases que rota
          son pedidos enteros ("Hacé un video de 10 segundos para Instagram…") y a 15px se
          cortaban a mitad de palabra en 390px —se veía escribir medio pedido y nunca el
          final—. Sólo el placeholder: lo que el usuario escribe sigue a 15px, que es el
          tamaño al que se lee y se corrige un texto propio. */}
      <div
        ref={cajaRef}
        className="[&_textarea]:placeholder:text-[rgb(var(--brand-black-rgb)_/_0.6)] max-sm:[&_textarea]:placeholder:text-[13.5px]"
      >
        <BarraEntradaChat
          valor={chat.borrador}
          onCambiarValor={chat.setBorrador}
          onEnviar={(texto) => {
            if (bloqueado) {
              onBloqueado?.();
              return;
            }
            chat.enviarMensaje(texto);
            onEnviado();
          }}
          streaming={chat.streaming}
          onDetener={chat.detener}
          cola={chat.cola}
          // Con un turno en curso la barra ENCOLA en vez de mandar. Desde la home eso
          // igual navega: el pedido ya entró a la conversación y quedarse mirando la home
          // sin ninguna señal de que pasó algo sería peor que llegar y ver la cola.
          onEncolar={(texto) => {
            if (bloqueado) {
              onBloqueado?.();
              return false;
            }
            const entro = chat.encolarMensaje(texto);
            if (entro) onEnviado();
            return entro;
          }}
          onQuitarDeCola={chat.quitarDeCola}
          interruptores={chat.interruptores}
          onCambiarInterruptor={chat.setInterruptor}
          preferencias={chat.preferencias}
          onCambiarPreferencia={chat.setPreferencia}
          adjuntos={chat.adjuntos}
          onAgregarAdjunto={chat.agregarAdjunto}
          onQuitarAdjunto={chat.quitarAdjunto}
          loteFotos={chat.loteFotos}
          onAdjuntarLote={chat.agregarLoteFotos}
          onQuitarLote={chat.quitarLoteFotos}
          onAbrirSelector={setSelector}
          companyId={companyId}
          inteligencia={chat.inteligencia}
          onCambiarInteligencia={chat.setInteligencia}
          puedeSubirInteligencia={chat.puedeSubirInteligencia}
          modeloDeImagen={chat.modeloDeImagen}
          onCambiarModeloDeImagen={chat.setModeloDeImagen}
          // SIN foco automático: en el chat la caja es la pantalla, acá es el principio de
          // una home que sigue para abajo. Enfocarla sola en desktop le roba el cursor a
          // quien vino a mirar sus creaciones, y en el teléfono levanta el teclado de
          // entrada y tapa media pantalla.
          autoFoco={false}
          // LAS PASTILLAS DE CONTENIDO, sólo acá
          // (ADR-2026-09-21-las-preferencias-salen-a-la-caja-como-pastillas): el switch
          // Imagen/Video y las preferencias de esa mitad, a la vista antes de escribir.
          // Esta caja es la primera cosa que se ve al entrar y era la única en la que
          // nada decía que esas opciones existían. Con las pastillas puestas, el menú del
          // "+" esconde su entrada "Preferencias de contenido" (lo hace la barra).
          pastillasDeContenido
          alto={variante === "pantalla" ? "amplio" : "normal"}
          // SIN el "+" — ADR-2026-09-21-la-caja-de-la-home-no-lleva-el-mas. La tarjeta de
          // la izquierda hace lo que hacía su menú (las seis fuentes y subir un archivo),
          // y lo que no adjuntaba ya subió a las pastillas de contenido: al lado de la
          // tarjeta, el "+" era un segundo botón para lo mismo.
          conBotonMas={false}
          variante={variante}
          placeholder={PLACEHOLDER_QUIETO}
          pedidoDeFoco={pedidoDeFoco}
          // La tarjeta es SÓLO de la caja de la home: el panel lateral y la pantalla del
          // chat montan la misma barra sin ella (allá la biblioteca hace este trabajo sin
          // tapar nada). Con el plan sin Agente no abre el selector —adjuntar no sirve de
          // nada si mandar no manda— y lleva a los planes, como el botón de enviar.
          tarjetaDeFuentes={
            <TarjetaDeFuentes
              chica={variante === "panel"}
              onAbrir={() => {
                if (bloqueado) {
                  onBloqueado?.();
                  return;
                }
                setFuentes(true);
              }}
            />
          }
        />
      </div>
      <ModalDeFuentes abierto={fuentes} onCerrar={() => setFuentes(false)} />
      <SelectoresDeAdjuntos
        abierto={selector}
        onCerrar={() => setSelector(null)}
        onAdjuntar={chat.agregarAdjunto}
        productos={entorno.productos}
        modelos={entorno.modelos}
        usuarioId={entorno.usuarioId}
        companyId={companyId}
        onModeloCreado={entorno.refrescarModelos}
      />
    </>
  );
};

export default CajaDelAgenteHome;
